import { useMemo, useState } from 'react';
import { Upload } from 'lucide-react';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { Button } from '@/components/ui/Button';
import { outreachAction } from '@/lib/outreach';
import { guessColumns, type ImportColumn, importProblem, IMPORT_ROLE_LABELS, type ImportRole, parseTable, tableBlock } from '@shared/table-import';

interface TableImportDrawerProps {
  toolAppId: string;
  /** A table handed over (from the chat), shown ready to check. */
  initialText?: string;
  onClose: () => void;
  onImported: () => void;
}

const ROLES = Object.keys(IMPORT_ROLE_LABELS) as ImportRole[];
const SKIPPED: Record<string, string> = { no_name: 'sans entreprise', ambiguous: 'entreprise ambiguë' };

/**
 * Importer un tableau (03/11/2026), as lemlist imports a CSV: paste from a
 * spreadsheet or pick a file, check what each column is, see the first rows,
 * import. A company already in the list is completed, never duplicated.
 */
export const TableImportDrawer = ({ toolAppId, initialText = '', onClose, onImported }: TableImportDrawerProps) => {
  const [text, setText] = useState(initialText);
  const [columns, setColumns] = useState<ImportColumn[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const table = useMemo(() => parseTable(tableBlock(text)), [text]);
  const [headers = [], ...rows] = table;
  const mapping = columns ?? guessColumns(headers);
  const problem = rows.length === 0 ? null : importProblem(mapping);

  const read = (value: string) => {
    setText(value);
    setColumns(null);
    setResult(null);
  };
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await outreachAction<{ created: number; updated: number; people: number; skipped: Array<{ row: number; reason: string }> }>({
        action: 'import_table', toolAppId, columns: mapping, rows,
      });
      const left = answer.skipped.length
        ? ` · ${answer.skipped.length} ligne(s) laissée(s) : ${[...new Set(answer.skipped.map((item) => SKIPPED[item.reason] ?? item.reason))].join(', ')}`
        : '';
      setResult(`${answer.created} entreprise(s) ajoutée(s), ${answer.updated} complétée(s), ${answer.people} personne(s)${left}`);
      onImported();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Import impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SideDrawer label="Importer un tableau" title="Importer un tableau" onClose={onClose}
      actions={rows.length > 0 && !result && (
        <Button size="sm" loading={busy} disabled={busy || Boolean(problem)} onClick={() => { void run(); }}>
          Importer {rows.length} ligne{rows.length > 1 ? 's' : ''}
        </Button>
      )}>
      <div className="space-y-4 p-4">
        {result && <p role="status" className="text-sm font-semibold text-domino-ink">{result}</p>}
        {error && <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>}
        {!result && (
          <>
            <div className="space-y-2">
              <textarea rows={6} value={text} onChange={(event) => read(event.target.value)} aria-label="Tableau à coller"
                placeholder="Coller un tableau, en-têtes compris"
                className="w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 font-mono text-xs text-domino-ink focus:border-domino-aubergine focus:outline-none" />
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-domino-aubergine hover:underline">
                <Upload className="h-4 w-4" /> Fichier CSV
                <input type="file" accept=".csv,.tsv,.txt,text/csv" className="sr-only"
                  onChange={(event) => { void event.target.files?.[0]?.text().then(read); }} />
              </label>
            </div>
            {headers.length > 0 && (
              <div className="space-y-2">
                {problem && <p className="text-xs text-domino-aubergine">{problem}</p>}
                <div className="overflow-x-auto rounded-xl border border-domino-cardBorder">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-domino-warm">
                      <tr>
                        {headers.map((header, index) => (
                          <th key={index} className="px-2 py-2 align-top font-medium text-domino-inkSoft">
                            <p className="mb-1 truncate">{header || `Colonne ${index + 1}`}</p>
                            <select value={mapping[index]?.as ?? 'parameter'} aria-label={`Ce qu’est « ${header} »`}
                              onChange={(event) => setColumns(mapping.map((column, at) => at === index ? { ...column, as: event.target.value as ImportRole } : column))}
                              className="rounded-md border border-domino-cardBorder bg-domino-white px-1.5 py-1 text-xs text-domino-ink">
                              {ROLES.map((role) => <option key={role} value={role}>{IMPORT_ROLE_LABELS[role]}</option>)}
                            </select>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 5).map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-t border-domino-cardBorder">
                          {headers.map((_, index) => (
                            <td key={index} className={`max-w-[12rem] truncate px-2 py-1.5 ${mapping[index]?.as === 'ignore' ? 'text-domino-inkSoft line-through' : 'text-domino-ink'}`}>{row[index] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length > 5 && <p className="text-xs text-domino-inkSoft">{rows.length - 5} autre(s) ligne(s)</p>}
              </div>
            )}
          </>
        )}
      </div>
    </SideDrawer>
  );
};
